import {
  ContentTypeEnum,
  ERROR_MESSAGES,
  LangEnum,
  OSFamilyEnum,
  ShowFormatEnum,
  SourcePagesEnum,
  SourceWidgetsEnum,
} from "@/constants/constants";
import {
  CategoryContentTypeEnum,
  MappedCategoryListingData,
  MicrodramaGenreItem,
  MicrodramaGenreRes,
} from "@/types/content.types";
import { fallbackCopies } from "@/constants/fallbackCopies";
import { ContentInterface } from "@/types/home.types";
import {
  captureError,
  generateContentDetailURL,
  generateURLSlug,
  getContentTypeUrlValue,
  getThumbnailsFromObject,
  getTrailerURLsFromSelectedPeripheral,
  interpolateStringValues,
} from "@/utils/common.helpers";
import {
  AllContentRes,
  GenreDetailItem,
  GenreDetailRes,
  MappedGenreData,
  PromotionClipData,
  ShowcaseGalleryData,
  ContentDeeplinkResponse,
} from "../../types/content.types";
import {
  GetContentWithCharactersResponse,
  ContentWithCharacters,
} from "@/types/chat.types";
import { API_ROUTES } from "../apiRoutes";
import { REQUEST_METHODS } from "../configs.apiClient";
import { HttpClient } from "../HttpClient";
import { LocalMediaStorage } from "@vidstack/react";

export class ContentAPIClient {
  private httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  // return list of all content in english
  async getAllContent({
    dialect,
    lang,
    type = "all",
    returnEnglishOnly = true,
    os,
  }: {
    dialect: string;
    lang: string;
    // API inconsistency workaround: API returns 'episode' instead of 'individual' type.
    type?: ContentTypeEnum.SHOW | "episode" | "all" | "allWithMicroDrama";
    returnEnglishOnly?: boolean;
    os?: OSFamilyEnum;
  }) {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.content.allContent;

      const response = await this.httpClient.nestRequest<AllContentRes>({
        endpoint,
        options: {
          method,
          headers: os ? { os } : undefined,
        },
        addToken: false,
        dialect,
        lang: lang,
      });

      const allContent = response.data
        .filter((item) => {
          // Filter by language: if returnEnglishOnly is true, only include English items
          const matchesLanguage =
            !returnEnglishOnly || item.displayLanguage === LangEnum.EN;

          // Filter by type: if type is "all" or "allWithMicroDrama", include all types; otherwise match specific type
          const matchesType =
            type === "all" ||
            type === "allWithMicroDrama" ||
            item.type === type;

          // Filter micro dramas: only include micro dramas when type is "allWithMicroDrama"
          // For non-micro dramas: always include (when other filters pass)
          // For micro dramas: only include if type === "allWithMicroDrama"
          const shouldIncludeItem =
            item.format !== ShowFormatEnum.MICRO_DRAMA ||
            type === "allWithMicroDrama";

          return matchesLanguage && matchesType && shouldIncludeItem;
        })
        .map((item) => {
          const slug = generateURLSlug({
            contentName: item.title,
            contentId: item._id,
          });

          const contentTypeUrlValue = getContentTypeUrlValue(item.type);

          const contentUrl = generateContentDetailURL(
            lang as LangEnum,
            dialect,
            contentTypeUrlValue,
            slug,
          );

          const thumbnails = getThumbnailsFromObject(
            item.thumbnail,
            item.type === ContentTypeEnum.SHOW,
            "small",
            "ratio1",
          );

          return { ...item, contentUrl, slug, ...thumbnails };
        });

      // return engAllContent.slice(0, 1);
      return allContent;
    } catch (error) {
      console.error("Error in ContentApiClient getAllContent", error);
      throw captureError(error);
    }
  }

  async getGenreDetail({
    lang,
    dialect,
    genreId,
  }: {
    lang: string;
    dialect: string;
    genreId: number;
  }): Promise<
    | (ShowcaseGalleryData & {
        genreName: string;
      })
    | { error: true }
  > {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.content.genreDetails;

      const response = await this.httpClient.nestRequest<GenreDetailRes>({
        endpoint,
        options: {
          method,
        },
        queryParams: {
          genreId,
        },
        addToken: true,
        dialect,
        lang: lang,
      });

      let englishResponse = response;

      if (lang !== LangEnum.EN) {
        englishResponse = await this.httpClient.nestRequest<GenreDetailRes>({
          endpoint,
          options: {
            method,
          },
          queryParams: {
            genreId,
          },
          addToken: true,
          dialect,
          lang: LangEnum.EN,
        });
      }

      const genreWiseData = response.genreWiseData[0];

      const mappedMovies = genreWiseData.movies.map((item) => {
        let englishTitle = item.title;

        if (lang !== LangEnum.EN) {
          englishTitle =
            englishResponse.genreWiseData[0].movies.find(
              (movie) => movie.slug === item.slug,
            )?.title || item.title;
        }

        const slug = generateURLSlug({
          contentName: englishTitle,
          fallbackSlug: item.slug,
          contentId: item._id,
        });

        const contentTypeUrlValue = getContentTypeUrlValue(item.contentType);

        const contentUrl = generateContentDetailURL(
          lang as LangEnum,
          dialect,
          contentTypeUrlValue,
          slug,
        );

        const thumbnails = getThumbnailsFromObject(
          item.thumbnail,
          item.contentType === ContentTypeEnum.SHOW,
          "small",
          "ratio1",
        );

        const selectedPeripheral = item.selectedPeripheral
          ? getTrailerURLsFromSelectedPeripheral({
              contentType: item.contentType,
              selectedPeripheral: item.selectedPeripheral,
              slug: item.slug,
            })
          : null;

        const contentItem: ContentInterface = {
          _id: item._id,
          title: item.title,
          description: item.description,
          contentUrl,
          type: item.contentType as ContentTypeEnum,
          slug,
          selectedPeripheral,
          ...thumbnails,
        };
        return contentItem;
      });

      const mappedShows = genreWiseData.shows.map((item) => {
        let englishTitle = item.title;

        if (lang !== LangEnum.EN) {
          englishTitle =
            englishResponse.genreWiseData[0].shows.find(
              (show) => show.slug === item.slug,
            )?.title || item.title;
        }

        const slug = generateURLSlug({
          contentName: englishTitle,
          fallbackSlug: item.slug,
          contentId: item._id,
        });

        const contentTypeUrlValue = getContentTypeUrlValue(item.contentType);

        const contentUrl = generateContentDetailURL(
          lang as LangEnum,
          dialect,
          contentTypeUrlValue,
          slug,
        );

        const thumbnails = getThumbnailsFromObject(
          item.thumbnail,
          item.contentType === ContentTypeEnum.SHOW,
          "small",
          "ratio1",
        );

        const selectedPeripheral = item.selectedPeripheral
          ? getTrailerURLsFromSelectedPeripheral({
              contentType: item.contentType,
              selectedPeripheral: item.selectedPeripheral,
              slug: item.slug,
            })
          : null;

        const contentItem: ContentInterface = {
          _id: item._id,
          title: item.title,
          contentUrl,
          type: item.contentType as ContentTypeEnum,
          slug,
          selectedPeripheral,
          description: item.description,
          ...thumbnails,
        };
        return contentItem;
      });

      return {
        title: interpolateStringValues(
          fallbackCopies[lang as LangEnum].genreDetail.heading,
          [genreWiseData.genreName],
        ),
        items: [...mappedMovies, ...mappedShows],
        genreName: genreWiseData.genreName,
        sourcePage: SourcePagesEnum.GENRE_DETAIL,
        sourceWidget: SourceWidgetsEnum.GENRE,
      };
    } catch (error) {
      console.error("Error in ContentApiClient getGenreDetail", error);
      captureError(error);
      return {
        error: true,
      };
    }
  }

  async getPromotionClip({
    contentId,
    contentType,
  }: {
    contentId?: number;
    contentType?: ContentTypeEnum;
  } = {}) {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.content.getPromotionClip;

      const queryParams =
        contentId && contentType
          ? {
              contentId,
              contentType,
            }
          : undefined;

      const response = await this.httpClient.nestRequest<PromotionClipData>({
        endpoint,
        options: {
          method,
        },
        queryParams,
      });

      return response;
    } catch (error) {
      console.error("Error in PaymentAPIClient getPromotionClip", error);
      throw captureError(error);
    }
  }

  async getContentWithCharacters({
    lang,
    dialect,
    token,
  }: {
    lang: string;
    dialect: string;
    token?: string;
  }): Promise<ContentWithCharacters[]> {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.content.contentWithCharacters;

      const response =
        await this.httpClient.nestRequest<GetContentWithCharactersResponse>({
          endpoint,
          options: {
            method,
          },
          addToken: true,
          dialect,
          lang,
          token,
        });

      return response.contents;
    } catch (error) {
      throw captureError(error);
    }
  }

  async getContentDeeplink({
    contentSlug,
    lang,
    dialect,
    platform = "app",
    os = "android",
    token,
  }: {
    contentSlug: string;
    lang: string;
    dialect: string;
    platform?: string;
    os?: string;
    token?: string;
  }): Promise<ContentDeeplinkResponse> {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.content.getContentDeeplink(contentSlug);

      const response =
        await this.httpClient.nestRequest<ContentDeeplinkResponse>({
          endpoint,
          options: {
            method,
            headers: {
              Accept: "application/json",
              os,
              platform,
            },
          },
          dialect,
          lang,
          addToken: true,
          token,
        });

      return response;
    } catch (error) {
      throw captureError(error);
    }
  }

  async getAllGenresGrouped({
    lang,
    dialect,
  }: {
    lang: string;
    dialect: string;
  }): Promise<MappedGenreData | { error: true }> {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.content.genreDetails;

      const response = await this.httpClient.nestRequest<GenreDetailRes>({
        endpoint,
        options: {
          method,
        },
        addToken: false,
        dialect,
        lang: lang,
      });

      // Also fetch English response for slug generation if not already English
      let englishResponse = response;
      if (lang !== LangEnum.EN) {
        englishResponse = await this.httpClient.nestRequest<GenreDetailRes>({
          endpoint,
          options: {
            method,
          },
          addToken: false,
          dialect,
          lang: LangEnum.EN,
        });
      }

      // Helper to map content items to ContentInterface
      const mapContentItem = (
        item: GenreDetailItem,
        englishItems?: GenreDetailItem[],
      ): ContentInterface => {
        const englishTitle =
          lang !== LangEnum.EN && englishItems
            ? englishItems.find((e) => e.slug === item.slug)?.title ||
              item.title
            : item.title;

        const slug = generateURLSlug({
          contentName: englishTitle,
          fallbackSlug: item.slug,
          contentId: item._id,
        });

        const contentUrl = generateContentDetailURL(
          lang as LangEnum,
          dialect,
          getContentTypeUrlValue(item.contentType),
          slug,
        );

        const thumbnails = getThumbnailsFromObject(
          item.thumbnail,
          item.contentType === ContentTypeEnum.SHOW,
          "small",
          "ratio1",
        );

        return {
          _id: item._id,
          title: item.title,
          description: item.description,
          type: item.contentType,
          contentUrl,
          slug,
          ...thumbnails,
        };
      };

      // Map the response to add contentUrl and thumbnails
      const mappedGenreWiseData = response.genreWiseData.map(
        (genreData, genreIndex) => {
          const englishGenreData = englishResponse.genreWiseData[genreIndex];

          return {
            ...genreData,
            movies: genreData.movies.map((item) =>
              mapContentItem(item, englishGenreData?.movies),
            ),
            shows: genreData.shows.map((item) =>
              mapContentItem(item, englishGenreData?.shows),
            ),
          };
        },
      );

      return {
        genreWiseData: mappedGenreWiseData,
      };
    } catch (error) {
      console.error("Error in ContentApiClient getAllGenresGrouped", error);
      captureError(error);
      return {
        error: true,
      };
    }
  }

  /**
   * Fetch category listing data for mWeb
   * @param contentType - movies, shows, or microdrama
   * @param lang - language code
   * @param dialect - dialect code
   * @param genreId - optional genre ID to filter by specific genre
   */
  async getCategoryListingData({
    contentType,
    lang,
    dialect,
    genreId,
  }: {
    contentType: CategoryContentTypeEnum;
    lang: string;
    dialect: string;
    genreId?: number;
  }): Promise<MappedCategoryListingData | { error: true; errorMsg: string }> {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.content.genreDetails;

      // Build query params based on content type
      const queryParams: Record<string, string | number> = {};

      if (contentType === CategoryContentTypeEnum.MOVIES) {
        queryParams.type = ContentTypeEnum.MOVIE;
      } else if (contentType === CategoryContentTypeEnum.SHOWS) {
        queryParams.type = ContentTypeEnum.SHOW;
      } else if (contentType === CategoryContentTypeEnum.MICRODRAMA) {
        queryParams.format = ShowFormatEnum.MICRO_DRAMA;
        queryParams.type = ContentTypeEnum.SHOW;
      }

      if (genreId) {
        queryParams.genreId = genreId;
      }

      // Microdrama uses a different response structure
      if (contentType === CategoryContentTypeEnum.MICRODRAMA) {
        const response = await this.httpClient.nestRequest<MicrodramaGenreRes>({
          endpoint,
          options: { method },
          queryParams,
          addToken: true,
          dialect,
          lang,
          cache: true,
        });

        let englishResponse = response;
        if (lang !== LangEnum.EN) {
          englishResponse =
            await this.httpClient.nestRequest<MicrodramaGenreRes>({
              endpoint,
              options: { method },
              queryParams,
              addToken: false,
              dialect,
              lang: LangEnum.EN,
            });
        }

        return this.mapMicrodramaResponse(
          response,
          lang,
          dialect,
          contentType,
          genreId,
        );
      }

      // For movies and shows - use the standard GenreDetailRes structure
      const response = await this.httpClient.nestRequest<GenreDetailRes>({
        endpoint,
        options: { method },
        queryParams,
        addToken: true,
        dialect,
        lang,
      });

      // Also fetch English response for slug generation if not already English
      let englishResponse = response;
      if (lang !== LangEnum.EN) {
        englishResponse = await this.httpClient.nestRequest<GenreDetailRes>({
          endpoint,
          options: { method },
          queryParams,
          addToken: false,
          dialect,
          lang: LangEnum.EN,
        });
      }

      return this.mapCategoryListingResponse(
        response,
        englishResponse,
        lang,
        dialect,
        contentType,
      );
    } catch (error) {
      console.error("Error in ContentApiClient getCategoryListingData", error);
      captureError(error);
      return {
        error: true,
        errorMsg: ERROR_MESSAGES.GENRE_DETAIL_API_ERROR,
      };
    }
  }

  private mapCategoryListingResponse(
    response: GenreDetailRes,
    englishResponse: GenreDetailRes,
    lang: string,
    dialect: string,
    contentType: CategoryContentTypeEnum,
  ): MappedCategoryListingData {
    const sections: MappedCategoryListingData["sections"] = [];
    const contentTypeLabel =
      contentType === CategoryContentTypeEnum.MOVIES
        ? "Movies"
        : contentType === CategoryContentTypeEnum.MICRODRAMA
          ? "Drama Shorts"
          : "Shows";

    response.genreWiseData.forEach((genreData, genreIndex) => {
      const englishGenreData = englishResponse.genreWiseData[genreIndex];
      // Movies use movies array, Shows and Microdrama use shows array
      const items =
        contentType === CategoryContentTypeEnum.MOVIES
          ? genreData.movies
          : genreData.shows;
      const englishItems =
        contentType === CategoryContentTypeEnum.MOVIES
          ? englishGenreData?.movies
          : englishGenreData?.shows;

      if (items && items.length > 0) {
        const mappedItems = items.map((item) => {
          const englishTitle =
            lang !== LangEnum.EN && englishItems
              ? englishItems.find((e) => e.slug === item.slug)?.title ||
                item.title
              : item.title;

          const slug = generateURLSlug({
            contentName: englishTitle,
            fallbackSlug: item.slug,
            contentId: item._id,
          });

          // For microdrama, use MICRO_DRAMA URL format
          const contentTypeForUrl =
            contentType === CategoryContentTypeEnum.MICRODRAMA
              ? ShowFormatEnum.MICRO_DRAMA
              : item.contentType;

          const contentUrl = generateContentDetailURL(
            lang as LangEnum,
            dialect,
            getContentTypeUrlValue(contentTypeForUrl),
            slug,
          );

          const thumbnails = getThumbnailsFromObject(
            item.thumbnail,
            item.contentType === ContentTypeEnum.SHOW ||
              contentType === CategoryContentTypeEnum.MICRODRAMA,
            "small",
            "ratio1",
          );

          const selectedPeripheral = item.selectedPeripheral
            ? getTrailerURLsFromSelectedPeripheral({
                contentType: item.contentType,
                selectedPeripheral: item.selectedPeripheral,
                slug: item.slug,
              })
            : null;

          return {
            _id: item._id,
            title: item.title,
            description: item.description,
            type: item.contentType,
            contentUrl,
            slug,
            selectedPeripheral,
            ...thumbnails,
          } as ContentInterface;
        });

        sections.push({
          title: `${genreData.genreName} ${contentTypeLabel}`,
          key: `genre-${genreData.genreId}-${contentType}`,
          items: mappedItems,
        });
      }
    });

    return { sections };
  }

  private mapMicrodramaResponse(
    response: MicrodramaGenreRes,
    lang: string,
    dialect: string,
    contentType: CategoryContentTypeEnum,
    genreId?: number,
  ): MappedCategoryListingData {
    const sections: MappedCategoryListingData["sections"] = [];

    const mapItems = (items: MicrodramaGenreItem[]) =>
      items.map((item) => {
        const slug = generateURLSlug({
          contentName: item.title,
          fallbackSlug: item.slug,
          contentId: item._id,
        });

        const contentTypeForUrl =
          item.contentType === ContentTypeEnum.SHOW
            ? ShowFormatEnum.MICRO_DRAMA
            : item.contentType;

        const contentUrl = generateContentDetailURL(
          lang as LangEnum,
          dialect,
          getContentTypeUrlValue(contentTypeForUrl),
          slug,
        );

        const thumbnails = getThumbnailsFromObject(
          item.thumbnail,
          true,
          "small",
          "ratio1",
        );

        const selectedPeripheral = item.selectedPeripheral
          ? getTrailerURLsFromSelectedPeripheral({
              contentType: item.contentType,
              selectedPeripheral: item.selectedPeripheral,
              slug: item.slug,
            })
          : null;

        return {
          _id: item._id,
          title: item.title,
          description: item.description,
          type: item.contentType,
          contentUrl,
          slug,
          selectedPeripheral,
          ...thumbnails,
        } as ContentInterface;
      });

    // Only add top 10 and trending when no specific genre is selected
    if (!genreId) {
      // Add top 10 microdramas first
      if (response.top10Microdramas?.data?.length > 0) {
        sections.push({
          title: response.top10Microdramas.title || "Top 10 Drama Shorts",
          key: "top10-microdramas",
          items: mapItems(response.top10Microdramas.data),
          isTop10: true,
        });
      }

      // Add trending microdramas
      if (response.trendingMicrodramas?.data?.length > 0) {
        sections.push({
          title: response.trendingMicrodramas.title || "Trending Drama Shorts",
          key: "trending-microdramas",
          items: mapItems(response.trendingMicrodramas.data),
          isTrending: true,
        });
      }
    }

    // Add genre-wise data
    response.genreWiseData?.forEach((genreData) => {
      if (genreData.microdramas && genreData.microdramas.length > 0) {
        sections.push({
          title: `${genreData.genreName || genreData.title} Drama Shorts`,
          key: `genre-${genreData.genreId || genreData._id}-microdrama`,
          items: mapItems(genreData.microdramas),
        });
      }
    });

    return { sections };
  }

  /**
   * Get list of all genres for a dialect
   */
  async getGenresList({
    lang,
    dialect,
  }: {
    lang: string;
    dialect: string;
  }): Promise<{ genreId: number; genreName: string }[] | { error: true }> {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.content.genreDetails;

      const response = await this.httpClient.nestRequest<GenreDetailRes>({
        endpoint,
        options: { method },
        addToken: false,
        dialect,
        lang,
        cache: true,
      });

      return response.genreWiseData.map((genre) => ({
        genreId: genre.genreId,
        genreName: genre.genreName,
      }));
    } catch (error) {
      console.error("Error in ContentApiClient getGenresList", error);
      captureError(error);
      return { error: true };
    }
  }
}
